import {
  createContext,
  useCallback,
  useContext,
  useRef,
  type ReactNode,
} from 'react';

import {
  getOrderFlowStartMs,
  trackOrderCreationStepCompleted,
  trackOrderCreationStepViewed,
  trackOrderPayRateInteracted,
  trackOrderSubmitted,
} from '@/util/vexo';
import type {
  OrderCreationStepKey,
  PayRateInputMethod,
  StaffingStepAggregates,
} from '@/util/vexo/events';

type PayRateArgs = {
  rate_entered: number;
  suggested_rate: number | null;
  delta_from_suggestion: number | null;
  input_method: PayRateInputMethod;
};

type SubmittedArgs = {
  order_id: string | number;
  shifts_required: number;
  pay_rate: number | null;
  shift_type: 'public' | 'invited' | null;
  region: string | null;
  total_value: number | null;
};

type TrackingApi = {
  trackStepViewed: (step: OrderCreationStepKey) => void;
  markStepCompleted: (
    step: OrderCreationStepKey,
    staffingAggregates?: StaffingStepAggregates,
  ) => void;
  trackPayRate: (args: PayRateArgs) => void;
  trackSubmitted: (args: SubmittedArgs) => void;
};

const noop = () => undefined;

const OrderCreationTrackingContext = createContext<TrackingApi>({
  trackStepViewed: noop,
  markStepCompleted: noop,
  trackPayRate: noop,
  trackSubmitted: noop,
});

export const useOrderCreationTracking = () =>
  useContext(OrderCreationTrackingContext);

export const OrderCreationTrackingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Flow clock was stamped at the entry-point click (Task 7). Fall back to now
  // if the user deep-linked past the entry surface so timings never go negative.
  const flowStartRef = useRef<number>(getOrderFlowStartMs() ?? Date.now());
  const stepStartRef = useRef<number>(Date.now());
  const lastViewedKeyRef = useRef<OrderCreationStepKey | null>(null);

  const trackStepViewed = useCallback((step: OrderCreationStepKey) => {
    // Consecutive-key dedup: a step re-rendering (or a StrictMode double mount)
    // must not double-fire its viewed event. Re-entering a step after visiting
    // a different one DOES re-fire, which is correct.
    if (lastViewedKeyRef.current === step) return;
    lastViewedKeyRef.current = step;
    stepStartRef.current = Date.now();
    trackOrderCreationStepViewed({
      step,
      time_since_flow_start_sec: Math.max(
        0,
        Math.round((Date.now() - flowStartRef.current) / 1000),
      ),
    });
  }, []);

  const markStepCompleted = useCallback(
    (
      step: OrderCreationStepKey,
      staffingAggregates?: StaffingStepAggregates,
    ) => {
      trackOrderCreationStepCompleted({
        step,
        time_on_step_sec: Math.max(
          0,
          Math.round((Date.now() - stepStartRef.current) / 1000),
        ),
        staffing_aggregates: staffingAggregates,
      });
    },
    [],
  );

  const trackPayRate = useCallback((args: PayRateArgs) => {
    // edit_count is wrapper-owned (events.ts flow store) post-Task-3.
    trackOrderPayRateInteracted(args);
  }, []);

  const trackSubmitted = useCallback((args: SubmittedArgs) => {
    trackOrderSubmitted({
      ...args,
      time_to_create_mins: Math.max(
        0,
        Math.round((Date.now() - flowStartRef.current) / 60_000),
      ),
    });
  }, []);

  return (
    <OrderCreationTrackingContext.Provider
      value={{
        trackStepViewed,
        markStepCompleted,
        trackPayRate,
        trackSubmitted,
      }}
    >
      {children}
    </OrderCreationTrackingContext.Provider>
  );
};
